import React, { useState, useEffect } from 'react';
import './Modals.css';
import { FaEye, FaEyeSlash, FaKey, FaCheck } from 'react-icons/fa';
import { generateRandomPassword, copyToClipboard } from '../utils/passwordGenerator';

function ResetPasswordModal({ user, onClose, onSave }) {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [copySuccess, setCopySuccess] = useState(false);

    // Password validation states
    const [validations, setValidations] = useState({
        hasUppercase: false,
        hasNumber: false,
        hasMinLength: false,
        passwordsMatch: false
    });

    // Check validations when inputs change
    useEffect(() => {
        const hasUppercase = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasMinLength = password.length >= 6;
        const passwordsMatch = password === confirmPassword && confirmPassword !== '';

        setValidations({
            hasUppercase,
            hasNumber,
            hasMinLength,
            passwordsMatch
        });

        // Enable button when all validations pass
        const allValidationsPassed = hasUppercase && hasNumber && hasMinLength && passwordsMatch;
        setIsButtonDisabled(!allValidationsPassed);
    }, [password, confirmPassword]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validations.hasUppercase || !validations.hasNumber || !validations.hasMinLength) {
            setError('La contraseña debe tener al menos una letra mayúscula, un número y 6 caracteres.');
            return;
        }

        if (!validations.passwordsMatch) {
            setError('Las contraseñas no coinciden.');
            return;
        }

        setLoading(true);

        const success = await onSave(user.wsp_email, password);

        if (!success) {
            setLoading(false);
        }
    };

    // Generate a random password and update both fields
    const handleGeneratePassword = async () => {
        const newPassword = generateRandomPassword(12); // 12-character password
        setPassword(newPassword);
        setConfirmPassword(newPassword);

        // Copy to clipboard
        const copied = await copyToClipboard(newPassword);
        if (copied) {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    return (
        <div className="modal-backdrop">
            <div className="modal-content">
                <h2>Restablecer Contraseña</h2>
                <p className="reset-email-text">
                    Estableciendo nueva contraseña para: <strong>{user.wsp_email}</strong>
                </p>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="password">Nueva contraseña</label>
                        <div className="password-input-container">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle-button"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex="-1"
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            <button
                                type="button"
                                className="generate-password-button"
                                onClick={handleGeneratePassword}
                                title="Generar contraseña aleatoria"
                                tabIndex="-1"
                                disabled={loading}
                            >
                                {copySuccess ? <FaCheck className="copy-success" /> : <FaKey />}
                            </button>
                        </div>
                        {password && (
                            <div className="password-requirements narrow-password-requirements">
                                <div className={validations.hasMinLength ? "requirement-met" : "requirement"}>
                                    Al menos 6 caracteres
                                </div>
                                <div className={validations.hasUppercase ? "requirement-met" : "requirement"}>
                                    Al menos una letra mayúscula
                                </div>
                                <div className={validations.hasNumber ? "requirement-met" : "requirement"}>
                                    Al menos un número
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirm-password">Confirmar nueva contraseña</label>
                        <div className="password-input-container">
                            <input
                                id="confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="password-toggle-button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                tabIndex="-1"
                            >
                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                        {confirmPassword && !validations.passwordsMatch && (
                            <div className="validation-error">
                                Las contraseñas no coinciden
                            </div>
                        )}
                    </div>

                    <div className="modal-buttons">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className={`save-button ${isButtonDisabled ? 'disabled' : ''}`}
                            disabled={isButtonDisabled || loading}
                        >
                            {loading ? 'Guardando...' : 'Guardar Contraseña'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ResetPasswordModal;